import * as React from 'react';

import { useQueryClient, MutationStatus } from '@tanstack/react-query';

import useGetAllowedActions from '../api/hooks/common/useGetAllowedActions';

import {
  AllowedActions,
  AuthorizationRequestType,
  FlightOperationNotification,
  FlightOperationNotificationDTO,
  FlightOperationNotificationVersionGroup,
  ReportType,
} from '../api/clients';

import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorOverlay from '../../components/ErrorOverlay';

import {
  useGetFlightOperationNotification,
  useGetFlightOperationNotificationAtVersion,
  useGetFlightOperationNotificationVersionGroups,
  useSubmitFlightOperationNotification,
  useUpdateFlightOperationNotification,
} from '../api/hooks/flight-operation';

import queryKeys from '../api/queryKeys';
import useTitle from '../api/hooks/common/useTitle';

type IFlightOperationNotificationData = {
  flightOperationNotification: FlightOperationNotification;
  flightOperationNotificationAtVersion: FlightOperationNotification | undefined;
  flightOperationNotificationVersionGroups: FlightOperationNotificationVersionGroup[];

  beginRequestAtVersion: (versionNumber: number) => void;
  selectedVersion: number | undefined;

  allowedActions: AllowedActions | undefined;

  update: (
    data: FlightOperationNotification,
    successCallback?: any,
    failCallback?: any
  ) => void;
  updateStatus: MutationStatus;

  submit: (
    eventId: number | undefined,
    successCallback?: any,
    failCallback?: any,
  ) => void;
  submitStatus: MutationStatus;
};

const FlightOperationNotificationDataContext = React.createContext<IFlightOperationNotificationData>(undefined!);

export function FlightOperationNotificationDataProvider({
  children,
  flightOperationNotificationId,
  role,
}: {
  children: React.ReactNode,
  flightOperationNotificationId: number,
  role?: AuthorizationRequestType,
}) {
  const queryClient = useQueryClient();
  useTitle(document.documentURI);
  const [requestedVersionNumber, setRequestedVersionNumber] = React.useState<number | undefined>(undefined);

  const {
    data: reportData,
    status: reportStatus,
    error: reportError,
  } = useGetFlightOperationNotification(flightOperationNotificationId, role || AuthorizationRequestType.Contribute);

  const {
    data: reportAtVersionData,
    status: reportAtVersionStatus,
    error: reportAtVersionError,
  } = useGetFlightOperationNotificationAtVersion(
    flightOperationNotificationId,
    requestedVersionNumber,
    role || AuthorizationRequestType.Read,
  );

  const {
    data: versionGroups,
    status: versionGroupsStatus,
    error: versionGroupsError,
  } = useGetFlightOperationNotificationVersionGroups(flightOperationNotificationId);

  const { data: allowedActions } = useGetAllowedActions(
    reportData?.employeeId,
    reportData?.status,
    ReportType.FlightOperationNotification,
  );

  const { status: updateMutationStatus, mutate: updateMutation } = useUpdateFlightOperationNotification();
  const { status: submitMutationStatus, mutate: submitMutation } = useSubmitFlightOperationNotification();

  const onSuccessfulMutation = (
    returnData: FlightOperationNotificationDTO,
    eventId: number | undefined,
    successCallback?: any,
    failCallback?: any,
  ) => {
    const { eventReport, error } = returnData;

    queryClient.setQueryData([queryKeys.FlightOperationNotification.Get, { id: eventId }], eventReport);
    if (!error) {
      if (successCallback) successCallback(eventReport);
    } else if (failCallback) failCallback(error);
  };

  const flightOperationNotificationData = React.useMemo<IFlightOperationNotificationData>(() => ({
    flightOperationNotification: reportData!,
    flightOperationNotificationAtVersion: reportAtVersionData!,
    flightOperationNotificationVersionGroups: versionGroups!,

    beginRequestAtVersion: (versionNumber: number) => setRequestedVersionNumber(versionNumber),
    selectedVersion: requestedVersionNumber,

    allowedActions: allowedActions!,

    updateStatus: updateMutationStatus,
    update: (data: FlightOperationNotification, successCallback?: any, failCallback?: any) => {
      updateMutation(data, {
        onSuccess: (returnData) => onSuccessfulMutation(
          returnData,
          data.id,
          successCallback,
          failCallback,
        ),
      });
    },

    submitStatus: submitMutationStatus,
    submit: (eventId: number | undefined, successCallback?: any, failCallback?: any) => {
      if (eventId !== undefined) {
        submitMutation(eventId, {
          onSuccess: (returnData) => onSuccessfulMutation(
            returnData,
            eventId,
            successCallback,
            failCallback,
          ),
        });
      }
    },
  }), [
    reportData,
    reportAtVersionData,
    versionGroups,
    allowedActions,
    updateMutationStatus,
    submitMutationStatus,
  ]);

  if (reportStatus === 'pending') {
    return (
      <LoadingOverlay />
    );
  }

  if (reportStatus === 'error') {
    return (
      <ErrorOverlay
        title={reportError?.title}
        message={`An error occurred retrieving data for form: ${flightOperationNotificationId}`}
      />
    );
  }

  if (reportStatus === 'success' && reportData !== undefined) {
    return (
      <FlightOperationNotificationDataContext.Provider value={flightOperationNotificationData}>
        {children}
      </FlightOperationNotificationDataContext.Provider>
    );
  }

  return null;
}

export const useFlightOperationNotificationData = () => React.useContext(FlightOperationNotificationDataContext);
